import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { editGenericOrder, getGenericOrderDetails, updateGenericOrderStatus } from "pages/GenericOrderDetailsPage/apis";
import { GenericOrderDetailResponse } from "pages/GenericOrderDetailsPage/types/order";
import NavbarWithActions from "components/Navbar/navbarwithactions";
import { Spinner } from "components";
import { User, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Option } from "pages/Opportunities/types";
import {
    AlertDialog,
    AlertDialogTrigger,
    AlertDialogPortal,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import LinkLeadDialog from "./linkleaddialoge";
// Same signature, answered by the new backend. See legacy-lookup-api.ts.
import { getRelationShipManager } from "service/moneeflo/legacy-lookup-api";
import ReactSelect from "react-select";
import { useSelectState } from "pages/Opportunities/utils/useSelect";

const GenericOrderPage = () => {
    const { id: orderId } = useParams<{ id: string }>();
    const [isLinkLeadOpen, setIsLinkLeadOpen] = useState(false);
    const [linkedLead, setLinkedLead] = useState<any>(null);

    const {
        data: orderDetails,
        isLoading,
        isError,
        refetch,
    } = useQuery<GenericOrderDetailResponse>(
        ["generic-order-details", orderId],
        () => getGenericOrderDetails(orderId as string),
        {
            enabled: !!orderId,
        }
    );

    const updateStatusMutation = useMutation(
        (status: string) =>
            updateGenericOrderStatus({
                // Not a number any more: "202611-000004" is an order number.
                order_id: orderId as string,
                status,
            }),
        {
            onSuccess: () => {
                refetch();
            },
            onError: (error) => {
                toast.error(
                    error instanceof Error && error.message
                        ? error.message
                        : "The order's status could not be changed",
                );
            },
        }
    );
    const {
        options: relationShipManagerOptions,
        selectedOption: relationShipManagerSelectedOption,
        handleChange: relationShipManagerHandleChange,
    } = useSelectState(
        [],
        null,
        () => { },
        () => { },
        fetchRelationShipManager as () => Promise<Option[]>,
    );
    if (isLoading) {
        return <Spinner />;
    }

    if (isError) {
        return (
            <div>
                <p>Failed to load order details</p>
                <button onClick={() => refetch()}>Retry</button>
            </div>
        );
    }

    const getStatusColor = (status?: string) => {
        switch (status?.toLowerCase()) {
            case "processing":
                return "text-purple-500";
            case "order successful":
                return "text-green-600";
            case "order cancelled":
                return "text-red-600";
            default:
                return "text-gray-600";
        }
    };


    const firstProduct = orderDetails?.product?.[0];
    const products = orderDetails?.product || [];

    const handleCloseOrder = () => {
        updateStatusMutation.mutate("close");
    };

    const handleCancelOrder = () => {
        updateStatusMutation.mutate("cancel");
    };

    const handleDoneClick = async () => {
        if (!relationShipManagerSelectedOption?.value) {
            toast.error("Please select a staff");
            return;
        }

        if (!orderDetails?.id) {
            toast.error("Invalid order");
            return;
        }

        try {
            await editGenericOrder({
                order_id: orderDetails.id,
                assign_to: relationShipManagerSelectedOption.value,
            });

            toast.success("Order reassigned successfully");

            await refetch(); // backend is source of truth
        } catch (error) {
            console.error(error);
            toast.error("Failed to reassign order");
        }
    };





    const formattedDate = new Date(orderDetails?.created_at).toLocaleString(
        "en-GB",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        }
    );
    async function fetchRelationShipManager(): Promise<void | Option[]> {
        // get auth token from localstorage
        const auth: string | null = localStorage.getItem("authToken");
        if (auth === null) return;
        const authToken = JSON.parse(auth);

        const req = {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        };
        const name = localStorage.getItem("name");
        const is_owner = localStorage.getItem("is_owner");
        const owner_name = localStorage.getItem("owner_name");
        const myself = localStorage.getItem("staff_id");

        try {
            const res = await getRelationShipManager(req);

            const options = res.data.map((obj) => ({
                label: obj?.fname,
                value: obj?.id,
            }));

            const index = options.findIndex((item) => item?.value == myself);
            if (index > 0) {
                const item = options.splice(index, 1)[0];
                options.unshift(item);
            }

            return options as Option[];
        } catch (error) {
            toast.error("Error fetching options staff");
        }
    }


    return (
        <div className="flex flex-col h-full overflow-hidden gap-2.5 font-inter">
            <NavbarWithActions header="Order Details" />
            <div className="flex overflow-auto flex-col h-full">
                <div className="flex h-full overflow-hidden md:overflow-auto gap-4 md:flex-col hide-scrollbar">
                    <div className="flex flex-col gap-5 min-h-full w-full overflow-y-scroll overflow-x-hidden p-3 bg-white-A700 border rounded-xl">
                        <div className="flex w-full gap-8 border-b-2 justify-between pb-5">
                            <div className="flex md:flex-col sm:flex-col  justify-between lg:items-start w-full">
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-row flex-wrap  items-center gap-2 capitalize">

                                        <h3 className="font-semibold whitespace-nowrap leading-4 text-lg text-black-label">
                                            OD-{orderDetails?.id}
                                        </h3>

                                        <span className="text-gray-400">•</span>

                                        <h3 className="font-semibold leading-4 text-lg text-black-label">
                                            {firstProduct?.created_for?.full_name}
                                        </h3>


                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <p className="text-[14px] font-normal text-[#7e7e7e]">Created on : {formattedDate} by {orderDetails?.staff?.fname || "-"}</p>
                                        <p className="text-[14px] font-normal text-[#7e7e7e]">Owner : {orderDetails?.owner?.fname} <span className="px-1"> • </span> Assigned to : { }
                                            {orderDetails?.assigned?.fname && (
                                                <span className="font-medium text-black-label mr-2">
                                                    {orderDetails.assigned.fname}
                                                </span>
                                            )}
                                            {!["Order Cancelled", "Order Successful"].includes(
                                                orderDetails?.maxx_status || "") && (
                                                    <AlertDialog>
                                                        <AlertDialogTrigger>
                                                            <div
                                                                className="cursor-pointer text-sm font-medium text-[#165FE3] underline "
                                                                style={{
                                                                    fontWeight: "600",
                                                                    fontSize: "14px",
                                                                }}
                                                            >
                                                                Reassign
                                                            </div>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogPortal>
                                                            <AlertDialogOverlay className="  bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                                                            <AlertDialogContent className="bg-white-A700 rounded-lg p-0 w-full animate-contentShow">
                                                                <AlertDialogHeader className="flex text-left border-b  rounded-t py-3 px-10 ">
                                                                    <AlertDialogTitle className="font-bold p-0 text-lg text-black-label">
                                                                        Select the staff to assign
                                                                    </AlertDialogTitle>
                                                                    <AlertDialogDescription></AlertDialogDescription>
                                                                </AlertDialogHeader>
                                                                <form className="px-10 w-full flex py-3 flex-col gap-2">
                                                                    <label
                                                                        htmlFor="staff_select"
                                                                        className=" text-sm font-medium "
                                                                    >
                                                                        Choose a staff
                                                                    </label>
                                                                    <ReactSelect
                                                                        menuPlacement="auto"
                                                                        className=""
                                                                        menuPosition="absolute"
                                                                        placeholder="Select the staff"
                                                                        styles={{
                                                                            control: (provided, state) => ({
                                                                                ...provided,
                                                                                zIndex: 0,
                                                                                borderRadius: ".5rem",
                                                                                padding: ".4rem",
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                borderColor: state.isFocused
                                                                                    ? "#165FE3"
                                                                                    : "#333333",
                                                                                boxShadow: "none",
                                                                            }),
                                                                            menu: (provided, state) => ({
                                                                                ...provided,
                                                                                zIndex: 1000,
                                                                            }),
                                                                        }}
                                                                        options={relationShipManagerOptions}
                                                                        onChange={relationShipManagerHandleChange}
                                                                        value={relationShipManagerSelectedOption}
                                                                        name="staff_select"
                                                                        id="staff_select"
                                                                    />
                                                                </form>
                                                                <AlertDialogFooter className="flex justify-start items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                                                                    <AlertDialogCancel
                                                                        className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                                                                        asChild
                                                                    >
                                                                        <button>Cancel</button>
                                                                    </AlertDialogCancel>
                                                                    <AlertDialogAction
                                                                        className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                                                                        asChild
                                                                    >
                                                                        <button
                                                                            onClick={handleDoneClick}
                                                                            disabled={!relationShipManagerSelectedOption}
                                                                        >
                                                                            Submit
                                                                        </button>

                                                                    </AlertDialogAction>
                                                                </AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialogPortal>
                                                    </AlertDialog>)}
                                            <span className="px-1"> •</span>  {orderDetails?.branch_name?.name}</p>
                                        {orderDetails?.invoice_no && (
                                            <p className="text-[14px] font-normal text-[#7e7e7e]">
                                                Invoice : {orderDetails.invoice_no}
                                                {orderDetails?.invoice_date && (
                                                    <>
                                                        <span className="px-1"> • </span>
                                                        {new Date(orderDetails.invoice_date).toLocaleDateString("en-IN", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}
                                                    </>
                                                )}
                                                {orderDetails?.invoice_url && (
                                                    <>
                                                        <span className="px-1"> • </span>
                                                        <a
                                                            href={orderDetails.invoice_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-700 underline"
                                                        >
                                                            View PDF
                                                        </a>
                                                    </>
                                                )}
                                            </p>
                                        )}
                                        <p className="text-[14px] font-normal text-[#7e7e7e]">
                                            Lead :{" "}
                                            {orderDetails?.linked_lead_id?.length > 0 ? (
                                                <span className="font-medium text-black-label">
                                                    {orderDetails.linked_lead_id[0]}
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => setIsLinkLeadOpen(true)}
                                                    className="text-[#165FE3] font-medium hover:underline"
                                                >
                                                    + Add
                                                </button>
                                            )}

                                        </p>

                                    </div>


                                </div>
                                <div className="flex gap-6 flex-col items-end">
                                    <div className="border  gap-2 w-fit rounded-xl px-4 py-2 flex items-center">

                                        <p
                                            className={`font-semibold text-sm ${updateStatusMutation.isLoading
                                                ? "text-gray-500"
                                                : getStatusColor(orderDetails?.maxx_status)
                                                }`}
                                        >
                                            {updateStatusMutation.isLoading
                                                ? "Updating status…"
                                                : orderDetails?.maxx_status
                                                    ?.charAt(0).toUpperCase() + orderDetails?.maxx_status?.slice(1)}
                                        </p>



                                    </div>
                                    {!["Order Cancelled", "Order Successful"].includes(
                                        orderDetails?.maxx_status || ""
                                    ) && (
                                            <div className="flex flex-row items-center gap-2">

                                                <AlertDialog>
                                                    {/* 🔹 Trigger */}
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-[#165FE3]"
                                                            disabled={updateStatusMutation.isLoading}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </AlertDialogTrigger>

                                                    <AlertDialogPortal>
                                                        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 fixed inset-0 animate-overlayShow" />

                                                        <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                                                            {/* 🔹 Header */}
                                                            <AlertDialogHeader className="flex flex-col text-left gap-2   p-4">
                                                                <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                                                                    Cancel this order?
                                                                </AlertDialogTitle>

                                                                <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E]">
                                                                    Are you sure you want to cancel this order? This action cannot be undone.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>

                                                            {/* 🔹 Footer */}
                                                            <AlertDialogFooter className="flex flex-row  justify-start items-center gap-2.5 py-2 px-5 rounded-b-lg bg-gray-button-gray-lite">
                                                                <AlertDialogCancel asChild>
                                                                    <button className="rounded-lg border mt-0 border-blue-700 py-[11px] px-4 text-blue-700">
                                                                        No, keep order
                                                                    </button>
                                                                </AlertDialogCancel>

                                                                <AlertDialogAction asChild>
                                                                    <button
                                                                        className="rounded-lg bg-blue-700 border border-blue-700 py-[11px] px-4 text-white-A700"
                                                                        onClick={handleCancelOrder}
                                                                    >
                                                                        Yes, cancel order
                                                                    </button>
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialogPortal>
                                                </AlertDialog>

                                                <AlertDialog>
                                                    {/* 🔹 Trigger */}
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            className="bg-[#165FE3] text-white-A700"
                                                            disabled={updateStatusMutation.isLoading}
                                                        >
                                                            Mark as successful
                                                        </Button>
                                                    </AlertDialogTrigger>

                                                    <AlertDialogPortal>
                                                        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 fixed inset-0 animate-overlayShow" />

                                                        <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                                                            {/* 🔹 Header */}
                                                            <AlertDialogHeader className="flex flex-col text-left gap-2 p-4">
                                                                <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                                                                    Close this order?
                                                                </AlertDialogTitle>

                                                                <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E]">
                                                                    Are you sure you want to close this order? You won’t be able to make any further changes.
                                                                </AlertDialogDescription>
                                                            </AlertDialogHeader>

                                                            {/* 🔹 Footer */}
                                                            <AlertDialogFooter className="flex flex-row justify-start items-center gap-2.5 py-2 px-5 rounded-b-lg bg-gray-button-gray-lite">
                                                                <AlertDialogCancel asChild>
                                                                    <button className="rounded-lg border mt-0 border-blue-700 py-[11px] px-4 text-blue-700">
                                                                       Cancel
                                                                    </button>
                                                                </AlertDialogCancel>

                                                                <AlertDialogAction asChild>
                                                                    <button
                                                                        className="rounded-lg bg-blue-700 border border-blue-700 py-[11px] px-4 text-white-A700"
                                                                        onClick={handleCloseOrder}
                                                                    >
                                                                       Mark successful
                                                                    </button>
                                                                </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialogPortal>
                                                </AlertDialog>

                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>

                        <div className="">
                            {/* Customer Details */}
                            <div className="border rounded-xl p-5">
                                <h3 className="font-semibold text-base mb-4">Customer Details</h3>

                                <div className="grid sm:block grid-cols-1 md:grid-cols-3 gap-4">


                                    {/* Email */}
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-gray-100">
                                            <Mail className="w-4 h-4 text-gray-600" />
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Email</p>
                                            <p className="font-medium break-all">
                                                {firstProduct?.created_for?.email || "-"}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Phone */}
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-gray-100">
                                            <Phone className="w-4 h-4 text-gray-600" />
                                        </div>

                                        <div>
                                            <p className="text-xs text-gray-500">Phone</p>
                                            <p className="font-medium">
                                                {firstProduct?.created_for?.phone_number || "-"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT PANEL - ORDER SUMMARY */}
                            <div className=" bg-white rounded-xl shadow-sm border p-6 h-fit sticky mt-5">


                                {/* PRODUCT BREAKDOWN */}
                                <div className="space-y-3 mb-4">
                                    {products.map((product) => {
                                        const productBase =
                                            product.inr_amount * product.quantity;

                                        const productTotal = productBase + product.gst;





                                        return (
                                            <div
                                                key={product.id}
                                                className="border rounded-lg p-3 text-sm"
                                            >
                                                <div className="font-semibold text-gray-900 capitalize">
                                                    {product.name}
                                                </div>

                                                <div className="text-xs text-gray-500 mb-1">
                                                    {product.subCategory}
                                                </div>

                                                <div className="flex justify-between text-gray-600 mt-1">
                                                    <span>Qty × Selling Price</span>
                                                    <span>
                                                        {product.quantity} × ₹{product.inr_amount.toFixed(2)}
                                                    </span>
                                                </div>

                                                <div className="flex justify-between text-gray-600">
                                                    <span>Subtotal</span>
                                                    <span>₹{productBase.toFixed(2)}</span>
                                                </div>

                                                <div className="flex justify-between text-gray-600">
                                                    <span>GST </span>
                                                    <span>₹ {Number(product.gst) }</span>

                                                </div>
                                                <div className="flex justify-between text-gray-600">
                                                    <span>Other </span>
                                                    <span>₹ {Number(product.other_charge) }</span>

                                                </div>


                                                <div className="flex justify-between font-semibold mt-1">
                                                    <span>Total</span>
                                                    <span>₹{Number(product.gst) + Number(product.other_charge) + Number(productBase.toFixed(2))}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* GRAND TOTAL */}
                                <div className="space-y-2 border-t pt-3">
                                    <div className="flex justify-between text-sm">
                                        <span>Subtotal</span>
                                        <span>₹{orderDetails?.amount_before_taxes.toFixed(2)}</span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                        <span>GST </span>
                                        <span>₹{orderDetails?.amount_and_charges.GST.GST}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>Other </span>
                                        <span>₹{orderDetails?.amount_and_charges.service_charge.amount}</span>
                                    </div>

                                    <div className="flex justify-between text-lg font-bold text-blue-600">
                                        <span>Grand Total</span>
                                        <span>₹{orderDetails?.total_payable.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <LinkLeadDialog
                open={isLinkLeadOpen}
                onOpenChange={setIsLinkLeadOpen}
                otherProps={orderDetails?.id}
                DealDetails={refetch}
                onDone={(partner) => {
                    setLinkedLead(partner);
                    setIsLinkLeadOpen(false);
                    // 🔥 call API to link lead here if needed
                }}
            />

        </div>
    );
};

export default GenericOrderPage;